import React, { useEffect } from 'react'
import { useRouter } from 'next/router'
import { connect, useDispatch } from 'react-redux'
import { SAVE_PRISMIC_DOCUMENTS } from '../redux/actions/prismic'
import Layout from '../components/Layout/Layout'
import { useTranslation } from 'react-i18next'
import { useMatomo } from '@datapunt/matomo-tracker-react'
import { RootState } from '../redux/reducers/rootReducer'
import { GetMapDispatchTypes } from '../interfaces/common/redux'
import { getGuestUserOrders, putUserOrder } from '../redux/actions/user'
import { createClient, getSingleDocument } from '../prismicio'
import { InferGetServerSidePropsType } from 'next/types'
import { PrismicAccount } from '../interfaces/common/prismicAccount'
import { setFullLocale } from '../utils/utils'
import { GetServerSideProps } from 'next'
import { getTranslations } from '../i18n'
import { prismicText } from '../components/Global/CMSText'
import { isSubscription } from '../utils/constants'
import clsx from 'clsx'
import { makeStyles, createStyles, Container } from '@material-ui/core'
import { Alert } from '@mui/material'
import SecondHandAndRentalOrderList from '../components/Order/SecondHandAndRentalOrderList'
import { postProductToReturn } from '../redux/actions/order'
import { getRightOfWithdrawalInfo } from '../redux/actions/takeBack'

const useOrdersStyles = makeStyles((theme) =>
  createStyles({
    container: {
      [theme.breakpoints.up('md')]: {
        padding: theme.spacing(8),
      },
    },

    background: {
      background: theme.extraColors.greyLight,
    },
  })
)

type Tprops = ReturnType<typeof mapStateToProps> &
  InferGetServerSidePropsType<typeof getServerSideProps> &
  GetMapDispatchTypes<typeof mapDispatchToProps>

const mapStateToProps = (state: RootState) => ({
  user: state.user,
  parameters: state.parameters,
})

const mapDispatchToProps = {
  getGuestUserOrders: getGuestUserOrders,
  getRightOfWithdrawalInfo: getRightOfWithdrawalInfo,
  postProductToReturn: postProductToReturn,
  putUserOrder: putUserOrder,
}

const Orders = ({
  user,
  getGuestUserOrders,
  getRightOfWithdrawalInfo,
  postProductToReturn,
  putUserOrder,
  doc,
  main,
  takeback,
  inStoreText,
  basket,
  faqRes,
  banner,
}: Tprops) => {
  const classes = useOrdersStyles()
  const router = useRouter()
  const { t } = useTranslation()
  const { trackPageView } = useMatomo()
  const dispatch = useDispatch()

  const handleGetGuestUserOrders = () => {
    const { cartToken, email } = router.query
    if (cartToken && email) {
      getGuestUserOrders(cartToken as string, email as string)
    }
  }

  useEffect(() => {
    dispatch(
      SAVE_PRISMIC_DOCUMENTS({
        general: main?.data,
        instore: inStoreText?.data,
      })
    )
  }, [main, inStoreText])

  useEffect(() => {
    trackPageView({ documentTitle: 'Orders' })
  }, [])

  useEffect(() => {
    handleGetGuestUserOrders()
  }, [router.query])

  return (
    <Layout
      mainLogo={main?.data?.logo}
      t={t}
      basket={basket}
      doc={main}
      inStoreText={inStoreText}
      metaTitle={prismicText(
        doc?.data?.meta_title_account,
        t('texts:account:herotitle')
      )}
      metaDescription={doc?.data?.meta_description_account}
      metaKeywords={doc?.data?.meta_keywords_account}
      takeback={takeback}
      account={doc}
      faq={faqRes}
      banner={banner}
    >
      <section className={clsx('account', classes.background)}>
        <Container
          className={classes.container}
          maxWidth={isSubscription ? 'xl' : 'lg'}
        >
          {user.error && (
            <Alert severity="error">{t('texts:orders:error')}</Alert>
          )}
          {user.orders && user.orders.length > 0 && (
            <SecondHandAndRentalOrderList
              takeback={takeback}
              orders={user?.orders}
              showSizes={main?.data?.show_size_name}
              putUserOrder={putUserOrder}
              getUserOrders={handleGetGuestUserOrders}
              getRightOfWithdrawalInfo={getRightOfWithdrawalInfo}
              postProductToReturn={postProductToReturn}
              prismicAccount={doc}
            />
          )}
        </Container>
      </section>
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps = async ({
  locale,
  previewData,
}) => {
  const client = createClient({ previewData })

  const lang = setFullLocale(locale)
  const takeback = await getSingleDocument(client, lang, 'take_back')
  const general = await getSingleDocument(client, lang, 'general')
  const account: PrismicAccount = await getSingleDocument(
    client,
    lang,
    'account'
  )
  const inStoreText = await getSingleDocument(client, lang, 'instore')
  const basket = await getSingleDocument(client, lang, 'basket')
  const faqRes = await getSingleDocument(client, lang, 'faq')
  const banner = await getSingleDocument(client, lang, 'banner')

  return {
    props: {
      main: general,
      doc: account,
      takeback,
      inStoreText,
      basket,
      faqRes,
      banner,
      i18nResources: await getTranslations(locale, ['texts']),
    },
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Orders)
